import { apiClient } from '@lib/api/client';
import type { ExcelReportRequest, ZReportRequest, BranchOfficeSearchResponse } from '../types/report.types';

export const reportService = {
  /**
   * Search branch offices for report filtering
   */
  async searchBranchOffices(companyId: string): Promise<BranchOfficeSearchResponse> {
    const response = await apiClient.post<BranchOfficeSearchResponse>('/reports/branch-offices/search', {
      CompanyId: companyId,
      PageNumber: 1,
      PageSize: 100,
      IsActive: true,
    });
    return response.data;
  },

  /**
   * Generate Excel report for issued documents.
   *
   * options.signal permite que Balance General gobierne su propio corte de
   * 90s con un AbortController propio -- sin esto, el timeout GLOBAL de
   * apiClient (65s, ver lib/api/client.ts) abortaria la espera antes de que
   * el esquema de aviso/corte escalonado tenga oportunidad de actuar.
   */
  async generateExcelReport(request: ExcelReportRequest, options?: { signal?: AbortSignal }): Promise<Blob> {
    const response = await apiClient.post<Blob>('/reports/excel', request, {
      responseType: 'blob',
      headers: {
        'Content-Type': 'application/json',
      },
      signal: options?.signal,
      // 0 = sin timeout propio de axios -- el AbortSignal (si se pasa) es
      // quien gobierna el corte. Sin options.signal, este override no
      // cambia nada (el default global de apiClient sigue aplicando).
      timeout: options?.signal ? 0 : undefined,
    });
    return response.data;
  },

  /**
   * Enviar el reporte Balance General por correo (flujo asincrono via Azure
   * Function). Salta directo el flujo de correo sin pasar por la descarga
   * sincrona -- usado tanto por el boton directo como por el esquema de
   * espera escalonado (aviso 30s / corte 90s) en handleGenerateBalanceSheet.
   */
  async sendBalanceSheetByEmail(
    request: Pick<ExcelReportRequest, 'FileName' | 'Filters'>,
    options?: { signal?: AbortSignal }
  ): Promise<{ uri: string | null }> {
    const response = await apiClient.post<{ uri: string | null }>(
      '/reports/balance-sheet/email',
      request,
      { signal: options?.signal }
    );
    return response.data;
  },

  /**
   * Generate Z Report (PDF)
   */
  async generateZReport(request: ZReportRequest): Promise<Blob> {
    const response = await apiClient.post<Blob>('/reports/build', request, {
      responseType: 'blob',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    return response.data;
  },

  /**
   * Download the Excel report
   */
  downloadExcelReport(blob: Blob, fileName: string): void {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${fileName}.xlsx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  },

  /**
   * Download the PDF report
   */
  downloadPdfReport(blob: Blob, fileName: string): void {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${fileName}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  },
};
